import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "../../../components/AppIcon";
import { cn } from "../../../utils/cn";
import { createDocumentFillerJob } from "../../../services/documentFillerService";
import useDocumentFillerSocket from "../../../hooks/useDocumentFillerSocket";
import { buildDocumentFillerFieldLabels } from "../../../utils/buildDocumentFillerFieldLabels";
import {
  buildDocumentFillerFormScaffold,
  buildDocumentFillerFormStructure,
} from "../../../utils/buildDocumentFillerFormScaffold";
import { applyDocumentFillerResult } from "../../../utils/applyDocumentFillerResult";

const INITIAL_ASSISTANT_MESSAGE =
  "Esta função de preenchimento está em estágio beta e pode cometer erros. Descreva as informações que deseja preencher e a NeoAi cuida do resto.\n\nPor enquanto o preenchimento tende a atualizar o formulário de forma ampla; atualizações parciais (por exemplo, só compradores ou só um bloco de campos, sem alterar o restante) chegarão em breve.";

const TEASER_TEXT = "Experimente usar a Neo Ai para preencher seu documento!";

const TEASER_STORAGE_KEY = "neoai-document-filler-teaser-dismissed";

const MAGIC_STATUS_LINES = [
  "Lendo suas informações…",
  "Mapeando os campos do formulário…",
  "Preenchendo os dados com a NeoAi…",
  "Conferindo nomes, documentos e valores…",
  "Quase lá — finalizando o preenchimento…",
];

function formatNeoCoins(value) {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) {
    const asText = String(value).trim();
    return asText || null;
  }
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function normalizeNeoCoins(raw) {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : String(raw).trim();
}
function FillMagicProgress() {
  const [statusIndex, setStatusIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % MAGIC_STATUS_LINES.length);
    }, 2800);
    return () => window.clearInterval(id);
  }, []);

  return (
    <motion.div
      key="document-filler-loading"
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="relative mr-2 overflow-hidden rounded-xl border border-primary/20 bg-background px-3 py-3 shadow-sm"
    >
      <motion.div
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent"
        animate={{ x: ["-120%", "120%"] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden
      />

      <div className="relative z-[1] flex items-start gap-3">
        <div className="relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center">
          <motion.span
            className="absolute inset-0 rounded-full bg-primary/15"
            animate={{ scale: [1, 1.25, 1], opacity: [0.55, 0.2, 0.55] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden
          />
          <motion.span
            animate={{ rotate: [0, 12, -8, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Icon name="Sparkles" size={18} className="text-primary" />
          </motion.span>
        </div>

        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="min-h-[1.25rem]">
            <AnimatePresence mode="wait">
              <motion.p
                key={statusIndex}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22 }}
                className="text-sm font-medium tracking-tight text-foreground"
              >
                {MAGIC_STATUS_LINES[statusIndex]}
              </motion.p>
            </AnimatePresence>
          </div>

          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <motion.div
              className="h-full rounded-full bg-primary"
              initial={{ width: "8%" }}
              animate={{ width: ["12%", "42%", "68%", "88%", "64%", "92%"] }}
              transition={{
                duration: 14,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>

          <div className="space-y-1.5" aria-hidden>
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-2">
                <motion.span
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                  animate={{ opacity: [0.25, 1, 0.25], scale: [0.85, 1.15, 0.85] }}
                  transition={{
                    duration: 1.1,
                    repeat: Infinity,
                    delay: row * 0.28,
                    ease: "easeInOut",
                  }}
                />
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted/80">
                  <motion.div
                    className="h-full rounded-full bg-primary/35"
                    animate={{ width: ["18%", "100%", "55%", "100%"] }}
                    transition={{
                      duration: 3.2,
                      repeat: Infinity,
                      delay: row * 0.45,
                      ease: "easeInOut",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground">
            A NeoAi está fazendo a mágica — isso pode levar alguns segundos.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

const DocumentFillerChatPanel = ({
  formData,
  setFormData,
  templateKey,
  formSchema,
  neoCoins: neoCoinsProp = null,
  onNeoCoinBalanceUpdate,
}) => {
  const panelRef = useRef(null);
  const activeJobIdRef = useRef(null);
  const neoCoinsRef = useRef(neoCoinsProp);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => [
    { role: "assistant", text: INITIAL_ASSISTANT_MESSAGE },
  ]);
  const [loading, setLoading] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const [neoCoins, setNeoCoins] = useState(neoCoinsProp);
  const listRef = useRef(null);

  useEffect(() => {
    neoCoinsRef.current = neoCoinsProp;
    setNeoCoins(neoCoinsProp);
  }, [neoCoinsProp]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (sessionStorage.getItem(TEASER_STORAGE_KEY) === "1") return undefined;

    const showTimer = window.setTimeout(() => setShowTeaser(true), 600);
    const hideTimer = window.setTimeout(() => setShowTeaser(false), 9000);
    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, []);

  const dismissTeaser = useCallback((persist = false) => {
    setShowTeaser(false);
    if (persist && typeof window !== "undefined") {
      sessionStorage.setItem(TEASER_STORAGE_KEY, "1");
    }
  }, []);

  const openPanel = useCallback(() => {
    dismissTeaser(true);
    setOpen(true);
  }, [dismissTeaser]);

  const appendMessage = useCallback((message) => {
    setMessages((previous) => [...previous, message]);
  }, []);

  const handleSocketMessage = useCallback(
    (payload) => {
      if (!payload || payload.job_id !== activeJobIdRef.current) return;

      if (payload.status === "done") {
        if (
          payload.result &&
          typeof payload.result === "object" &&
          !Array.isArray(payload.result)
        ) {
          setFormData((prev) =>
            applyDocumentFillerResult(prev, payload.result, {
              formSchema,
              templateKey,
            })
          );
          const previousBalance = neoCoinsRef.current;
          const nextBalance = normalizeNeoCoins(payload.coin_balance);
          if (nextBalance !== null) {
            neoCoinsRef.current = nextBalance;
            setNeoCoins(nextBalance);
            onNeoCoinBalanceUpdate?.(nextBalance);
          }

          const previousLabel = formatNeoCoins(previousBalance);
          const nextLabel = formatNeoCoins(nextBalance);
          let balanceNote = "";
          if (previousLabel && nextLabel) {
            balanceNote = `\n\nNeo Coins: ${previousLabel} → ${nextLabel}`;
          } else if (nextLabel) {
            balanceNote = `\n\nSaldo atual: ${nextLabel} Neo Coins`;
          }

          appendMessage({
            role: "assistant",
            text: `Pronto! Revise e ajuste os campos preenchidos antes de gerar o documento.${balanceNote}`,
          });
        } else {
          appendMessage({
            role: "assistant",
            text: "O preenchimento foi concluído, mas não retornou dados para aplicar.",
          });
        }
        activeJobIdRef.current = null;
        setLoading(false);
        // Collapse so filled form fields stay visible under the chat overlay.
        setOpen(false);
        return;
      }

      if (payload.status === "failed") {
        activeJobIdRef.current = null;
        setLoading(false);
        appendMessage({
          role: "assistant",
          text: "Não foi possível preencher o documento. Tente novamente.",
        });
      }
    },
    [appendMessage, formSchema, onNeoCoinBalanceUpdate, setFormData, templateKey]
  );

  const { status: socketStatus, reconnect } = useDocumentFillerSocket({
    enabled: open,
    onMessage: handleSocketMessage,
  });

  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, open, loading]);

  useEffect(() => {
    if (!open || loading) return undefined;

    const handlePointerDownOutside = (event) => {
      if (!panelRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("touchstart", handlePointerDownOutside, {
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
    };
  }, [loading, open]);

  useEffect(() => {
    if (!loading || socketStatus !== "disconnected") return;

    activeJobIdRef.current = null;
    setLoading(false);
    appendMessage({
      role: "assistant",
      text: "A conexão foi perdida antes da resposta. Reconecte e tente novamente.",
    });
  }, [appendMessage, loading, socketStatus]);

  const handleCancelWait = useCallback(() => {
    activeJobIdRef.current = null;
    setLoading(false);
    appendMessage({
      role: "assistant",
      text: "A espera pela resposta foi cancelada. Você pode enviar um novo pedido.",
    });
  }, [appendMessage]);

  const handleSend = useCallback(async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;

    if (!templateKey) {
      appendMessage({
        role: "assistant",
        text: "Selecione um modelo de documento antes de pedir o preenchimento.",
      });
      return;
    }

    if (socketStatus !== "connected") {
      appendMessage({
        role: "assistant",
        text: "Aguarde a conexão com o NeoAi antes de enviar o pedido.",
      });
      return;
    }

    setInput("");
    appendMessage({ role: "user", text: prompt });

    const fieldLabels = buildDocumentFillerFieldLabels(formSchema);
    const formStructure = buildDocumentFillerFormStructure(formSchema);
    const scaffoldedFormData = buildDocumentFillerFormScaffold(
      formSchema,
      formData
    );
    const result = await createDocumentFillerJob({
      userData: prompt,
      formData: scaffoldedFormData,
      templateKey,
      fieldLabels,
      formStructure,
    });

    if (!result.ok) {
      appendMessage({
        role: "assistant",
        text:
          result.message ||
          "Não foi possível iniciar o preenchimento. Tente novamente.",
        insufficientCoins: result.insufficientCoins,
      });
      return;
    }

    activeJobIdRef.current = result.jobId;
    setLoading(true);
  }, [
    appendMessage,
    formData,
    formSchema,
    input,
    loading,
    socketStatus,
    templateKey,
  ]);

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      ref={panelRef}
      className={cn(
        // Mobile: left side, clear of preview FAB (right) and speed dial (bottom-right)
        "fixed z-[45] flex flex-col items-start",
        "left-3 sm:left-4",
        "bottom-[calc(6.5rem+env(safe-area-inset-bottom,0px))]",
        // Desktop: dock over the preview column so form fields stay readable
        "lg:bottom-6 lg:left-auto lg:right-6 lg:items-end",
        open && "w-[min(22.5rem,calc(100vw-1.5rem))]"
      )}
    >
      <AnimatePresence mode="wait">
        {open ? (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="flex w-full flex-col max-h-[min(72vh,30rem)] overflow-hidden rounded-2xl border border-border bg-card shadow-elevation-lg"
          >
            <div className="flex shrink-0 items-center justify-between gap-2 bg-primary px-3 py-2.5 text-primary-foreground">
              <span className="flex items-center gap-2 text-sm font-semibold tracking-tight">
                <Icon name="Sparkles" size={18} />
                NeoAi
              </span>
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center gap-1 rounded-md bg-primary-foreground/15 px-2 py-1 text-xs font-medium tabular-nums"
                  title="Saldo de Neo Coins"
                  aria-label={`Saldo de Neo Coins: ${formatNeoCoins(neoCoins) ?? "carregando"}`}
                >
                  <Icon name="Coins" size={14} aria-hidden />
                  {formatNeoCoins(neoCoins) ?? "…"}
                </span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-md p-1.5 hover:bg-primary-foreground/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/40 disabled:opacity-50"
                  aria-label="Recolher NeoAi"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>
            <div
              ref={listRef}
              className="min-h-[180px] max-h-[min(44vh,19rem)] flex-1 space-y-3 overflow-y-auto bg-muted/20 px-3 py-2"
            >
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm leading-relaxed",
                    message.role === "user"
                      ? "ml-4 bg-primary/10 text-foreground"
                      : "mr-2 border border-border bg-background"
                  )}
                >
                  <span className="whitespace-pre-wrap">{message.text}</span>
                  {message.insufficientCoins ? (
                    <Link
                      to="/license-store"
                      className="mt-2 inline-flex text-sm font-medium text-primary underline"
                    >
                      Comprar Neo Coins
                    </Link>
                  ) : null}
                </div>
              ))}
              <AnimatePresence mode="wait">
                {loading ? <FillMagicProgress /> : null}
              </AnimatePresence>
            </div>

            <div className="shrink-0 border-t border-border bg-card p-2">
              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={onKeyDown}
                rows={2}
                disabled={loading || !templateKey}
                placeholder={
                  templateKey
                    ? "Ex.: Preencha com os dados da compra do imóvel…"
                    : "Selecione um modelo para começar…"
                }
                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={
                  loading ||
                  !input.trim() ||
                  !templateKey ||
                  socketStatus !== "connected"
                }
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Icon name="Send" size={16} />
                <span>Preencher documento</span>
              </button>
              {loading ? (
                <button
                  type="button"
                  onClick={handleCancelWait}
                  className="mt-2 text-xs font-medium text-primary underline"
                >
                  Cancelar espera
                </button>
              ) : socketStatus === "connecting" ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Conectando ao NeoAi…
                </p>
              ) : socketStatus === "disconnected" ? (
                <button
                  type="button"
                  onClick={reconnect}
                  className="mt-2 text-xs font-medium text-primary underline"
                >
                  Reconectar ao NeoAi
                </button>
              ) : null}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="launcher"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="relative flex flex-col items-start gap-2 lg:items-end"
          >
            <AnimatePresence>
              {showTeaser ? (
                <motion.div
                  key="teaser"
                  initial={{ opacity: 0, y: 12, scale: 0.92 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.96 }}
                  transition={{
                    type: "spring",
                    stiffness: 380,
                    damping: 24,
                  }}
                  className="relative mb-1 max-w-[16.5rem] sm:max-w-[18rem]"
                >
                  <div className="relative rounded-2xl border border-primary/25 bg-card px-3.5 py-3 pr-8 text-sm font-medium leading-snug text-foreground shadow-elevation-md">
                    <motion.span
                      className="absolute inset-0 rounded-2xl bg-primary/10"
                      animate={{ opacity: [0.35, 0.7, 0.35] }}
                      transition={{
                        duration: 2.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      aria-hidden
                    />
                    <span className="relative z-[1]">{TEASER_TEXT}</span>
                    <button
                      type="button"
                      onClick={() => dismissTeaser(true)}
                      className="absolute right-1.5 top-1.5 z-[2] rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label="Dispensar dica"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                  <div
                    className="ml-6 h-0 w-0 border-x-[7px] border-t-[8px] border-x-transparent border-t-card drop-shadow-sm"
                    aria-hidden
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>

            <motion.button
              type="button"
              onClick={openPanel}
              className="relative inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-elevation-md transition-colors hover:bg-muted"
              aria-label="Abrir NeoAi para preencher documento"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.span
                className="absolute -inset-1 rounded-full border border-primary/35"
                animate={{ scale: [1, 1.12, 1], opacity: [0.55, 0, 0.55] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
                aria-hidden
              />
              <Icon name="Sparkles" size={20} className="relative text-primary" />
              <span className="relative">NeoAi</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentFillerChatPanel;
